import type {
  CarClass,
  CreateChallengeBody,
  CreateTournamentBody,
  Duel,
  DuelResultSubmission,
  LandingPulse,
  PendingTierChange,
  PlayerProfile,
  RaceType,
  Ranking,
  RankingGroup,
  RankingSyncLog,
  Report,
  DismissReportBody,
  OpenDisputeBody,
  RulesSection,
  Tier,
  SubmitDuelResultBody,
  SubmitMatchBody,
  SubmitRaceResultsBody,
  SubmitReportBody,
  Tournament,
  TournamentParticipant,
  TournamentStandings,
  TournamentStatus,
  UpdateTournamentBody,
  User,
  VoucherState,
} from "system";

import { getAccessToken } from "./supabase";

const BASE = "/api";

function qs(params?: Record<string, string | number | undefined>): string {
  if (!params) return "";
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined) sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export class ApiResponseError extends Error {
  constructor(
    public status: number,
    message: string,
    public data: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getAccessToken();

  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(async () => {
      const text = await res.text().catch(() => "");
      return { error: text || res.statusText || "Unknown error" };
    }) as Record<string, unknown>;
    throw new ApiResponseError(res.status, (body.error as string) || res.statusText || "Unknown error", body);
  }

  return res.json() as Promise<T>;
}

// ── Auth ──────────────────────────────────────────────────────────────────────

export const authApi = {
  sync: (token: string, providerToken?: string | null) =>
    request<User>("/auth/sync", { method: "POST", body: JSON.stringify({ token, providerToken }) }),
  me: () => request<User>("/auth/me"),
};

// ── Rankings ──────────────────────────────────────────────────────────────────

export const rankingsApi = {
  list: (params?: { raceType?: string; carClass?: string; tier?: string; search?: string }) =>
    request<Ranking[]>(`/rankings${qs(params)}`),
  groups: (params?: { raceType?: string; carClass?: string; search?: string }) =>
    request<RankingGroup[]>(`/rankings/groups${qs(params)}`),
  counts: () => request<string[]>("/rankings/counts"),
  rivals: () => request<Array<Ranking & { user: User }>>("/rankings/rivals"),
};

// ── Players ───────────────────────────────────────────────────────────────────

export const playersApi = {
  list: () => request<User[]>("/players"),
  get: (id: string) => request<PlayerProfile>(`/players/${id}`),
  getByUsername: (username: string) =>
    request<PlayerProfile>(`/players/by-username/${encodeURIComponent(username)}`),
  updateMe: (body: { gamertag: string | null }) =>
    request<User>("/players/me", { method: "PATCH", body: JSON.stringify(body) }),
};

export const ogApi = {
  upload: (username: string, blob: Blob, key: string) =>
    request<{ ok: true }>(
      `/og/${encodeURIComponent(username)}?key=${encodeURIComponent(key)}`,
      { method: "POST", body: blob, headers: { "Content-Type": "image/png" } },
    ),
};

// ── Config ────────────────────────────────────────────────────────────────────

export const configApi = {
  get: () => request<{ sheetsUrl: string | null }>("/config"),
};

// ── Duels ─────────────────────────────────────────────────────────────────────

export const duelsApi = {
  list: (params?: { status?: string; playerId?: string; page?: number }) =>
    request<Duel[]>(`/duels${qs(params)}`),
  get: (id: string) => request<Duel>(`/duels/${id}`),
  challenge: (body: CreateChallengeBody) =>
    request<Duel>("/duels", { method: "POST", body: JSON.stringify(body) }),
  accept: (id: string) =>
    request<{ ok: boolean }>(`/duels/${id}/accept`, { method: "POST" }),
  decline: (id: string, reason?: string) =>
    request<{ ok: boolean }>(`/duels/${id}/decline`, { method: "POST", body: JSON.stringify({ reason }) }),
  forfeit: (id: string) =>
    request<{ ok: boolean; outcome: "tie" | "forfeit_loss" }>(`/duels/${id}/forfeit`, { method: "POST" }),
  submitMatch: (id: string, body: SubmitMatchBody) =>
    request<{ ok: boolean; attackerScore: number; defenderScore: number }>(
      `/duels/${id}/matches`, { method: "POST", body: JSON.stringify(body) },
    ),
  submitResult: (id: string, body: SubmitDuelResultBody) =>
    request<{ ok: boolean; waiting?: boolean; finalized?: boolean } & Partial<DuelResultSubmission>>(
      `/duels/${id}/result`, { method: "POST", body: JSON.stringify(body) },
    ),
  dispute: (id: string) =>
    request<{ ok: boolean }>(`/duels/${id}/dispute`, { method: "POST" }),
  resolveDispute: (id: string, verdict: "attacker" | "defender" | "replay") =>
    request<{ ok: boolean }>(`/duels/${id}/resolve-dispute`, { method: "POST", body: JSON.stringify({ verdict }) }),
};

// ── Tournaments ───────────────────────────────────────────────────────────────

export const tournamentsApi = {
  list: (params?: { status?: string }) =>
    request<Tournament[]>(`/tournaments${qs(params)}`),
  get: (id: string) => request<Tournament>(`/tournaments/${id}`),
  standings: (id: string) => request<TournamentStandings>(`/tournaments/${id}/standings`),
  create: (body: CreateTournamentBody) =>
    request<Tournament>("/tournaments", { method: "POST", body: JSON.stringify(body) }),
  join: (id: string) =>
    request<{ ok: boolean }>(`/tournaments/${id}/join`, { method: "POST" }),
  leave: (id: string) =>
    request<{ ok: boolean }>(`/tournaments/${id}/leave`, { method: "POST" }),
  submitResults: (id: string, raceNumber: number, body: SubmitRaceResultsBody) =>
    request<{ ok: boolean }>(`/tournaments/${id}/races/${raceNumber}/results`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  deleteResults: (id: string, raceNumber: number) =>
    request<{ ok: boolean }>(`/tournaments/${id}/races/${raceNumber}/results`, {
      method: "DELETE",
    }),
  complete: (id: string) =>
    request<{ ok: boolean; promotedCount: number }>(`/tournaments/${id}/complete`, { method: "POST" }),
  updateStatus: (id: string, status: TournamentStatus) =>
    request<Tournament>(`/tournaments/${id}`, { method: "PATCH", body: JSON.stringify({ status }) }),
  update: (id: string, body: UpdateTournamentBody) =>
    request<Tournament>(`/tournaments/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  addParticipant: (id: string, userId: string) =>
    request<TournamentParticipant>(`/tournaments/${id}/participants`, { method: "POST", body: JSON.stringify({ userId }) }),
  removeParticipant: (id: string, userId: string) =>
    request<{ ok: boolean }>(`/tournaments/${id}/participants/${userId}`, { method: "DELETE" }),
};

// ── Reports ───────────────────────────────────────────────────────────────────

export const reportsApi = {
  list: (params?: { status?: string }) =>
    request<Report[]>(`/reports${qs(params)}`),
  get: (id: string) => request<Report>(`/reports/${id}`),
  create: (body: SubmitReportBody) =>
    request<Report>("/reports", { method: "POST", body: JSON.stringify(body) }),
  assign: (id: string) =>
    request<Report>(`/reports/${id}/assign`, { method: "POST" }),
  release: (id: string) =>
    request<Report>(`/reports/${id}/release`, { method: "POST" }),
  dismiss: (id: string, body: DismissReportBody) =>
    request<Report>(`/reports/${id}/dismiss`, { method: "POST", body: JSON.stringify(body) }),
  openDispute: (id: string, body?: OpenDisputeBody) =>
    request<Report>(`/reports/${id}/open-dispute`, { method: "POST", body: JSON.stringify(body ?? {}) }),
  force: (id: string, body?: OpenDisputeBody) =>
    request<Report>(`/reports/${id}/force`, { method: "POST", body: JSON.stringify(body ?? {}) }),
  escalate: (id: string) =>
    request<Report>(`/reports/${id}/escalate`, { method: "POST" }),
};

// ── Vouchers ──────────────────────────────────────────────────────────────────

export const vouchersApi = {
  list: () => request<VoucherState>("/vouchers"),
  activateAny: () => request<{ vacationUntil: string }>("/vouchers/activate-any", { method: "POST" }),
  activate: (id: string) => request<{ vacationUntil: string }>(`/vouchers/${id}/activate`, { method: "POST" }),
  grant: (userId: string, count = 1) =>
    request<{ granted: number }>(`/vouchers/grant/${userId}`, { method: "POST", body: JSON.stringify({ count }) }),
  retract: (userId: string, count: number) =>
    request<{ retracted: number }>(`/vouchers/retract/${userId}`, { method: "POST", body: JSON.stringify({ count }) }),
};

// ── Admin ─────────────────────────────────────────────────────────────────────

export const adminApi = {
  sync: () => request<{ rowsAffected: number }>("/admin/sync", { method: "POST" }),
  syncHistory: () => request<RankingSyncLog[]>("/admin/sync/history"),
  /** @deprecated Overdue-duel expiry runs automatically on the scheduler tick (server/lib/scheduler.ts). No UI calls this; kept as a manual escape hatch. */
  expireDuels: () => request<{ expired: number }>("/admin/expire-duels", { method: "POST" }),
  config: () => request<{
    masterModeEnabled: boolean;
    masterModeActive: boolean;
  }>("/admin/config"),
  setMasterMode: (enable: boolean) =>
    request<{ masterMode: boolean }>("/admin/config/master-mode", {
      method: "POST",
      body: JSON.stringify({ enable }),
    }),
  pendingTierChanges: () => request<PendingTierChange[]>("/admin/pending-tier-changes"),
  setTier: (body: { userId: string; raceType: RaceType; carClass: CarClass; toTier: Tier; reason: string; expectedFromTier?: Tier | null }) =>
    request<{ ok: boolean; applied: boolean }>("/admin/rankings/set-tier", { method: "POST", body: JSON.stringify(body) }),
  disqualify: (body: { userId: string; reason: string }) =>
    request<{ ok: boolean; removed: number }>("/admin/rankings/disqualify", { method: "POST", body: JSON.stringify(body) }),
  reseed: (body: { userId: string; reason: string }) =>
    request<{ ok: boolean; seeded: number }>("/admin/rankings/reseed", { method: "POST", body: JSON.stringify(body) }),
  uploadToSheet: () =>
    request<{ written: number; skipped: boolean }>("/admin/rankings/upload-to-sheet", { method: "POST" }),
  analytics: () => request<{
    totalPlayers: number;
    totalDuels: number;
    completedDuels: number;
    activeDuels: number;
    disputedDuels: number;
    totalMatches: number;
  }>("/admin/analytics"),
};

// ── Landing ───────────────────────────────────────────────────────────────────

export const landingApi = {
  stats: () => request<LandingPulse>("/landing/stats", { method: "GET" })
};

// ── Rules ─────────────────────────────────────────────────────────────────────

export const rulesApi = {
  get: () => request<RulesSection[]>("/rules"),
};